import { useWaysteClient } from '@alliance-disposal/client';
import type { DisposalSite } from '@alliance-disposal/transport-types';
import { toast } from '@wayste/sour-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DISPOSAL_SITE_QUERY_KEYS } from './query-keys';

type UseDisposalSitesParams = {
    enabled?: boolean;
};

/**
 * Hook to manage fetching all disposal sites for the current hauler.
 * Pass `enabled: false` to defer fetching until a condition is met.
 */
export const useDisposalSites = ({ enabled = true }: UseDisposalSitesParams = {}) => {
    const client = useWaysteClient();
    const queryClient = useQueryClient();

    const {
        data: disposalSites,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: [DISPOSAL_SITE_QUERY_KEYS.disposalSites],
        enabled,
        queryFn: async () => {
            try {
                return await client.disposalSite().wayste.query();
            } catch (err) {
                console.error('Error fetching disposal sites:', err);
                toast.error('Failed to load disposal sites');
                throw err;
            }
        },
    });

    const invalidateListQuery = () => {
        queryClient.invalidateQueries({ queryKey: [DISPOSAL_SITE_QUERY_KEYS.disposalSites] });
    };

    return {
        disposalSites: disposalSites || [],
        isLoading,
        isError,
        error,
        invalidateListQuery,
        refetch,
    };
};

export const useCreateDisposalSite = () => {
    const client = useWaysteClient();
    const queryClient = useQueryClient();

    const createDisposalSiteMutation = useMutation({
        // Deep-copied before sending, matching the behaviour this replaces.
        mutationFn: (data: DisposalSite.DisposalSiteCreateInput) => client.disposalSite().wayste.create(JSON.parse(JSON.stringify(data))),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [DISPOSAL_SITE_QUERY_KEYS.disposalSites] }),
        onError: () => toast.error('An error occurred creating the disposal site. Try reloading the browser and trying again.'),
    });

    return {
        createDisposalSite: createDisposalSiteMutation.mutateAsync,
        isCreatingDisposalSite: createDisposalSiteMutation.isPending,
    };
};
